import React from 'react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { CloudArrowUp, FileArrowDown } from '@phosphor-icons/react';
import { formatCurrency, formatDate } from '../../lib/utils';

export const EMBSExportPreviewDialog = ({ open, onOpenChange, previewData, onExport, exporting }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle style={{ fontFamily: 'Manrope' }}>eMBS Export Preview</DialogTitle>
        <DialogDescription>Preview claims before exporting to Ohio eMBS portal</DialogDescription>
      </DialogHeader>

      {previewData && (
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-4 p-4 bg-slate-50 rounded-lg">
            <div>
              <p className="text-sm text-slate-500">Claims to Export</p>
              <p className="text-2xl font-bold text-indigo-900">{previewData.claim_count}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Total Amount</p>
              <p className="text-2xl font-bold text-emerald-600 font-mono">{formatCurrency(previewData.total_amount)}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Contract #</p>
              <p className="text-2xl font-bold font-mono">{previewData.contract_number}</p>
            </div>
          </div>

          <Card className="border-indigo-200 bg-indigo-50">
            <CardContent className="pt-4">
              <p className="text-sm text-indigo-700"><strong>Format:</strong> {previewData.file_format}</p>
              <p className="text-xs text-indigo-600 mt-1">Fixed-width flat file for Ohio eMBS portal bulk upload</p>
            </CardContent>
          </Card>

          <div className="max-h-64 overflow-y-auto border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Patient</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Code</TableHead>
                  <TableHead>eMBS Code</TableHead>
                  <TableHead>Units</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>County</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {previewData.claims?.map((claim, idx) => (
                  <TableRow key={claim.id || claim.claim_number || idx}>
                    <TableCell className="font-medium">{claim.patient_name}</TableCell>
                    <TableCell>{formatDate(claim.service_date)}</TableCell>
                    <TableCell className="font-mono">{claim.service_code}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="bg-indigo-50 text-indigo-700">
                        {claim.embs_service_code}
                      </Badge>
                    </TableCell>
                    <TableCell>{claim.units}</TableCell>
                    <TableCell className="font-mono">{formatCurrency(claim.amount)}</TableCell>
                    <TableCell>{claim.county} ({claim.county_code})</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {previewData.claims?.[0] && (
            <div className="space-y-2">
              <Label className="text-sm text-slate-500">Sample Flat File Line:</Label>
              <div className="p-3 bg-slate-900 rounded-lg overflow-x-auto">
                <code className="text-xs text-green-400 font-mono whitespace-pre">{previewData.claims[0].flat_file_line}</code>
              </div>
            </div>
          )}
        </div>
      )}

      <DialogFooter className="gap-2">
        <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
        <Button variant="outline" onClick={() => onExport('csv')} disabled={exporting}>
          <FileArrowDown size={16} className="mr-2" />
          Export CSV
        </Button>
        <Button
          className="bg-indigo-900 hover:bg-indigo-800"
          onClick={() => onExport('flat_file')}
          disabled={exporting}
          data-testid="confirm-export-btn"
        >
          {exporting ? (
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <>
              <CloudArrowUp size={16} className="mr-2" />
              Export eMBS Flat File
            </>
          )}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
